import CircularLoader from "@/components/loading/LoadingComponent";
import { fetchWithCSRF } from "@/helpers/common/csrf";
import { useAuxiliaryData } from "@/hooks/general/useAuxillaryData";
import { utcEndOfDay, utcStartOfDay } from "@/utils/utcConverters";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "tablewind";
import { BulkAction, ColumnConfig, Player } from "./_types";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const fetcher = (url: string) => fetchWithCSRF(url).then((res) => res.json());

export default function PlayersPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const { loading, allClans } = useAuxiliaryData();

    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setUTCDate(today.getUTCDate() - 30);

    const initialStartDate = utcStartOfDay(thirtyDaysAgo).toISOString();
    const initialEndDate = utcEndOfDay(today).toISOString();

    const defaultParams: Record<string, string> = {
        page: "1",
        page_size: "25",
        sort_by: "shield__shield_number",
        order: "asc",
    };

    const urlParams = Object.fromEntries(searchParams.entries());

    const initialParams: Record<string, string> = {
        ...defaultParams,
        ...urlParams,
    };

    // --- A refresh key to force the DataTable to re-mount ---
    const [refreshKey, setRefreshKey] = useState(0);
    const triggerRefresh = useCallback(() => {
        setRefreshKey((prev) => prev + 1);
    }, []);

    // --- Inline Editing Save Handler ---
    const handleEditSave = async (id: string, newValues: Partial<Player>) => {
        try {
            let body = JSON.stringify({
                donation_amount: newValues.donation_amount,
                merch_amount: newValues.merch_amount,
                moderator: newValues.moderator,
                vip: newValues.vip,
                permanent_vip: newValues.permanent_vip,
                tag_of_vanderwood: newValues.tag_of_vanderwood,
                golden_woh_token: newValues.golden_woh_token,
                tag_of_ivar_tier:
                    newValues.tag_of_ivar_tier !== undefined
                        ? Number(newValues.tag_of_ivar_tier)
                        : undefined,
                gifted_subs: newValues.gifted_subs,
                raid_tickets: newValues.raid_tickets,
                bits: newValues.bits,
            });

            const res = await fetchWithCSRF(
                `${API_URL}/longhouse-api/village/players/${id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: body,
                },
            );
            if (!res.ok) {
                console.error("Error saving post");
            } else {
                triggerRefresh();
            }
        } catch (error) {
            console.error("Error saving post", error);
        }
    };

    // ---------------------------------------------------------------------
    // BULK ACTION HANDLERS – these functions perform the bulk updates directly
    // ---------------------------------------------------------------------
    const handleBulkAddGoldenWOHToken = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-add-golden-woh-token`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk add golden WOH token");
                }
            } catch (error) {
                console.error(
                    "Error performing bulk add golden WOH token",
                    error,
                );
            }
        },
        [triggerRefresh],
    );

    const handleBulkRemoveGoldenWOHToken = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-remove-golden-woh-token`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error(
                        "Error performing bulk remove golden WOH token",
                    );
                }
            } catch (error) {
                console.error(
                    "Error performing bulk remove golden WOH token",
                    error,
                );
            }
        },
        [triggerRefresh],
    );

    const handleBulkAddTagOfVanderwood = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-add-tag-of-vanderwood`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error(
                        "Error performing bulk add tag of Vanderwood",
                    );
                }
            } catch (error) {
                console.error(
                    "Error performing bulk add tag of Vanderwood",
                    error,
                );
            }
        },
        [triggerRefresh],
    );

    const handleBulkRemoveTagOfVanderwood = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-remove-tag-of-vanderwood`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error(
                        "Error performing bulk remove tag of Vanderwood",
                    );
                }
            } catch (error) {
                console.error(
                    "Error performing bulk remove tag of Vanderwood",
                    error,
                );
            }
        },
        [triggerRefresh],
    );

    const handleBulkAddModerator = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-add-moderator`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk add moderator");
                }
            } catch (error) {
                console.error("Error performing bulk add moderator", error);
            }
        },
        [triggerRefresh],
    );

    const handleBulkRemoveModerator = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-remove-moderator`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk remove moderator");
                }
            } catch (error) {
                console.error("Error performing bulk remove moderator", error);
            }
        },
        [triggerRefresh],
    );

    const handleBulkAddVIP = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-add-vip`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk add VIP");
                }
            } catch (error) {
                console.error("Error performing bulk add VIP", error);
            }
        },
        [triggerRefresh],
    );

    const handleBulkRemoveVIP = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-remove-vip`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk remove VIP");
                }
            } catch (error) {
                console.error("Error performing bulk remove VIP", error);
            }
        },
        [triggerRefresh],
    );

    const handleBulkAddPermanentVIP = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-add-permanent-vip`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk add permanent VIP");
                }
            } catch (error) {
                console.error("Error performing bulk add permanent VIP", error);
            }
        },
        [triggerRefresh],
    );

    const handleBulkRemovePermanentVIP = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-remove-permanent-vip`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk remove permanent VIP");
                }
            } catch (error) {
                console.error(
                    "Error performing bulk remove permanent VIP",
                    error,
                );
            }
        },
        [triggerRefresh],
    );

    const handleBulkRemoveShield = useCallback(
        async (selectedIds: string[]) => {
            try {
                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/players/bulk-remove-shield`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ player_ids: selectedIds }),
                    },
                );
                if (res.ok) {
                    triggerRefresh();
                } else {
                    console.error("Error performing bulk remove shield");
                }
            } catch (error) {
                console.error("Error performing bulk remove shield", error);
            }
        },
        [triggerRefresh],
    );

    const filterFields = useMemo(
        () => [
            {
                name: "clan",
                apiKey: "clans",
                allOption: "All Clans",
                type: "select" as const,
            },
            {
                name: "has_shield",
                apiKey: "has_shield",
                allOption: "Shield Status (All Players)",
                type: "select" as const,
                options: [
                    { id: "true", name: "Has Shield" },
                    { id: "false", name: "No Shield" },
                ],
            },
            {
                name: "moderator",
                apiKey: "moderator",
                allOption: "Moderator Status (All Players)",
                type: "select" as const,
                options: [
                    { id: "true", name: "Moderators" },
                    { id: "false", name: "Non-Moderators" },
                ],
            },
            {
                name: "vip",
                apiKey: "VIP",
                allOption: "VIP Status (All Players)",
                type: "select" as const,
                options: [
                    { id: "vip", name: "VIPs" },
                    { id: "permanent_vip", name: "Permanent VIPs" },
                    { id: "non_vip", name: "Non-VIPs" },
                ],
            },
            {
                name: "tag_of_vanderwood",
                apiKey: "tag_of_vanderwood",
                allOption: "Tag of Vanderwood Status (All Players)",
                type: "select" as const,
                options: [
                    { id: "true", name: "Has Tag of Vanderwood" },
                    { id: "false", name: "No Tag of Vanderwood" },
                ],
            },
            {
                name: "golden_woh_token",
                apiKey: "golden_woh_token",
                allOption: "Golden WOH Token Status (All Players)",
                type: "select" as const,
                options: [
                    { id: "true", name: "Has Golden WOH Token" },
                    { id: "false", name: "No Golden WOH Token" },
                ],
            },
        ],
        [],
    );

    const columns = useMemo(() => {
        const baseColumns: ColumnConfig<Player>[] = [
            {
                accessor: "shield_number",
                label: "Shield Number",
                sortable: true,
                editable: false,
                sortKey: "shield__shield_number",
                render: (row: Player) => row.shield_number || "N/A",
            },
            {
                accessor: "name",
                label: "Player Name",
                sortable: true,
                editable: false,
                sortKey: "name",
                render: (row: Player) => (
                    <div className="flex items-center truncate">
                        <Link
                            to={`/dashboard/players/${row.id}`}
                            className="flex items-center truncate hover:underline"
                        >
                            <img
                                src={row.shield_image_url || "/no-shield.png"}
                                alt={row.name}
                                className="mr-4 h-9 w-10 min-w-10 rounded-md object-cover"
                            />
                            <div className="truncate">{row.name}</div>
                        </Link>
                    </div>
                ),
            },
            {
                accessor: "clan.tag",
                label: "Clan",
                sortable: false,
                editable: false,
            },
            {
                accessor: "shield_value",
                label: "Shield Value",
                sortable: true,
                editable: false,
                render: (row: Player) =>
                    row.shield_value !== null
                        ? row.shield_value.toString()
                        : "N/A",
            },
            {
                accessor: "current_season_vp",
                label: "Current Season VP",
                sortable: true,
                editable: false,
                // Lets format with commas for better readability
                render: (row: Player) =>
                    row.current_season_vp !== null
                        ? row.current_season_vp.toLocaleString()
                        : "N/A",
            },
            {
                accessor: "lifetime_vp",
                label: "Lifetime VP",
                sortable: true,
                editable: false,
                render: (row: Player) =>
                    row.lifetime_vp !== null
                        ? row.lifetime_vp.toLocaleString()
                        : "N/A",
            },
            {
                accessor: "donation_amount",
                label: "Donation Amount",
                sortable: true,
                editable: true,
                sortKey: "donation__amount",
                render: (row: Player) =>
                    row.donation_amount !== null
                        ? "$" +
                          row.donation_amount.toLocaleString() +
                          " " +
                          "AUD"
                        : "N/A",
                inputType: "text" as const,
            },
            {
                accessor: "merch_amount",
                label: "Merch Amount",
                sortable: true,
                editable: true,
                sortKey: "merchandise__amount",
                render: (row: Player) =>
                    row.merch_amount !== null
                        ? "$" + row.merch_amount.toLocaleString() + " " + "AUD"
                        : "N/A",
                inputType: "text" as const,
            },
            {
                accessor: "gifted_subs",
                label: "Gifted Subs",
                sortable: true,
                editable: true,
                sortKey: "gifted_subscriptions__gifted_subs",
                render: (row: Player) =>
                    row.gifted_subs !== null
                        ? row.gifted_subs.toLocaleString()
                        : "0",
                inputType: "text" as const,
            },
            {
                accessor: "raid_tickets",
                label: "Raid Tickets",
                sortable: true,
                editable: true,
                sortKey: "raid_tickets__tickets",
                render: (row: Player) =>
                    row.raid_tickets !== null
                        ? row.raid_tickets.toLocaleString()
                        : "0",
                inputType: "text" as const,
            },
            {
                accessor: "bits",
                label: "Bits",
                sortable: true,
                editable: true,
                sortKey: "bits__bits",
                render: (row: Player) =>
                    row.bits !== null ? row.bits.toLocaleString() : "0",
                inputType: "text" as const,
            },
            {
                accessor: "moderator",
                label: "Moderator",
                editable: true,
                sortable: false,
                render: (row: Player) => (row.moderator ? "Yes" : "No"),
                inputType: "checkbox" as const,
            },
            {
                accessor: "vip",
                label: "VIP",
                editable: true,
                sortable: false,
                render: (row: Player) => (row.vip ? "Yes" : "No"),
                inputType: "checkbox" as const,
            },
            {
                accessor: "permanent_vip",
                label: "Permanent VIP",
                editable: true,
                sortable: false,
                render: (row: Player) => (row.permanent_vip ? "Yes" : "No"),
                inputType: "checkbox" as const,
            },
            {
                accessor: "tag_of_vanderwood",
                label: "Tag of Vanderwood",
                editable: true,
                sortable: false,
                render: (row: Player) => (row.tag_of_vanderwood ? "Yes" : "No"),
                inputType: "checkbox" as const,
            },
            {
                accessor: "golden_woh_token",
                label: "Golden WOH Token",
                editable: true,
                sortable: false,
                render: (row: Player) => (row.golden_woh_token ? "Yes" : "No"),
                inputType: "checkbox" as const,
            },
            {
                accessor: "tag_of_ivar_tier",
                label: "Tag of IVAR",
                editable: true,
                sortable: true,
                sortKey: "tag_of_ivar__tier",
                render: (row: Player) => {
                    const tierLabelByValue: Record<number, string> = {
                        0: "Tier 0",
                        1: "Tier 1",
                        70: "Tier 2",
                        80: "Tier 3",
                        90: "Tier 4",
                        100: "Tier 5",
                    };

                    return tierLabelByValue[row.tag_of_ivar_tier] || "Tier 0";
                },
                inputType: "select" as const,
                options: [
                    { id: "0", name: "Tier 0" },
                    { id: "1", name: "Tier 1" },
                    { id: "70", name: "Tier 2" },
                    { id: "80", name: "Tier 3" },
                    { id: "90", name: "Tier 4" },
                    { id: "100", name: "Tier 5" },
                ],
            },
            {
                accessor: "exalted_level",
                label: "Exalted Level",
                sortable: true,
                editable: false,
                render: (row: Player) => row.exalted_level.toString(),
            },
            {
                accessor: "wheel_of_hamingja_wins",
                label: "WOH Wins",
                sortable: true,
                editable: false,
                render: (row: Player) => row.wheel_of_hamingja_wins.toString(),
            },
            {
                accessor: "wheel_of_hamingja_tokens",
                label: "WOH Tokens",
                sortable: true,
                editable: false,
                render: (row: Player) =>
                    row.wheel_of_hamingja_tokens.toString(),
            },
            {
                accessor: "shield_mates_count",
                label: "Shield Mates",
                sortable: true,
                editable: false,
                render: (row: Player) => row.shield_mates_count.toString(),
            },
        ];

        return baseColumns;
    }, []);

    const bulkActions = useMemo(() => {
        const baseActions: BulkAction<Player>[] = [
            {
                key: "add-golden-woh-token",
                label: "Add Golden WOH Token",
                onClick: handleBulkAddGoldenWOHToken,
            },
            {
                key: "remove-golden-woh-token",
                label: "Remove Golden WOH Token",
                onClick: handleBulkRemoveGoldenWOHToken,
            },
            {
                key: "add-tag-of-vanderwood",
                label: "Add Tag of Vanderwood",
                onClick: handleBulkAddTagOfVanderwood,
            },
            {
                key: "remove-tag-of-vanderwood",
                label: "Remove Tag of Vanderwood",
                onClick: handleBulkRemoveTagOfVanderwood,
            },
            {
                key: "add-moderator",
                label: "Add Moderator",
                onClick: handleBulkAddModerator,
            },
            {
                key: "remove-moderator",
                label: "Remove Moderator",
                onClick: handleBulkRemoveModerator,
            },
            {
                key: "add-vip",
                label: "Add VIP",
                onClick: handleBulkAddVIP,
            },
            {
                key: "remove-vip",
                label: "Remove VIP",
                onClick: handleBulkRemoveVIP,
            },
            {
                key: "add-permanent-vip",
                label: "Add Permanent VIP",
                onClick: handleBulkAddPermanentVIP,
            },
            {
                key: "remove-permanent-vip",
                label: "Remove Permanent VIP",
                onClick: handleBulkRemovePermanentVIP,
            },
            {
                key: "remove-shield",
                label: "Remove Shield",
                onClick: handleBulkRemoveShield,
            },
        ];

        return baseActions;
    }, []);

    if (loading) {
        return <CircularLoader />;
    }

    return (
        <>
            <DataTable<Player>
                navigate={navigate}
                pageTitle="Players"
                pageSubtitle="View the Battle of Midgard players."
                key={refreshKey}
                endpoint={`${API_URL}/longhouse-api/village/players/all`}
                columns={columns}
                onEditSave={handleEditSave}
                initialQuery={initialParams}
                fetcher={fetcher}
                filterFields={filterFields}
                bulkActions={bulkActions}
                loadingComponent={<CircularLoader />}
                showMobileFilters={showMobileFilters}
                setShowMobileFilters={setShowMobileFilters}
                searchEnabled={true}
                searchPlaceholder="Search by player name"
            />
        </>
    );
}
